/**
 * Settings Page - Full User Profile & Preferences Management
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User, Lock, Bell, Globe, Palette, Shield, Save, Mail, Phone, Key } from 'lucide-react';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const user = useAuthStore(state => state.user);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('profile');

  // Profile form
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: '',
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Preferences form
  const [preferencesForm, setPreferencesForm] = useState({
    theme: 'light',
    language: 'en',
    timezone: 'UTC',
    notifications: {
      email: true,
      push: true,
      sms: false,
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: any) => {
      return api.put(`/api/v1/users/${user?.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      toast.success('Profile updated successfully! ✅');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to update profile');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: any) => {
      return api.post('/api/v1/auth/change-password', data);
    },
    onSuccess: () => {
      toast.success('Password changed successfully! 🔐');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to change password');
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileForm);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'preferences', label: 'Preferences', icon: Palette },
  ];

  return (
    <div className="w-full min-h-screen p-6 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-3">
          <User className="w-10 h-10 text-primary-600" />
          Settings
        </h1>
        <p className="text-secondary-600 dark:text-secondary-400 mt-2 text-lg">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl p-4 space-y-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white shadow-lg'
                    : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl p-8">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6 animate-fade-in">
                <div className="flex items-center gap-4 pb-6 border-b border-secondary-200 dark:border-secondary-700">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl shadow-xl">
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {user?.firstName} {user?.lastName}
                    </h2>
                    <p className="text-secondary-600 dark:text-secondary-400">{user?.email}</p>
                  </div>
                </div>

                <form onSubmit={handleProfileSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.firstName}
                        onChange={e =>
                          setProfileForm({ ...profileForm, firstName: e.target.value })
                        }
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profileForm.lastName}
                        onChange={e => setProfileForm({ ...profileForm, lastName: e.target.value })}
                        className="input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="input"
                      placeholder="+44 123 456 7890"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={updateProfileMutation.isPending}
                    className="bg-gradient-to-r from-primary-600 to-purple-600 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-200 font-semibold disabled:opacity-50"
                  >
                    <Save className="w-5 h-5" />
                    {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6 animate-fade-in">
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-white pb-4 border-b border-secondary-200 dark:border-secondary-700">
                  Change Password
                </h2>

                <form onSubmit={handlePasswordSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={e =>
                        setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                      }
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={e =>
                        setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                      }
                      className="input"
                      required
                      minLength={8}
                    />
                    <p className="text-xs text-secondary-500 mt-1">
                      Must be at least 8 characters long
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={e =>
                        setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                      }
                      className="input"
                      required
                      minLength={8}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={changePasswordMutation.isPending}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-200 font-semibold disabled:opacity-50"
                  >
                    <Shield className="w-5 h-5" />
                    {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6 animate-fade-in">
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-white pb-4 border-b border-secondary-200 dark:border-secondary-700">
                  Notification Preferences
                </h2>

                <div className="space-y-4">
                  {[
                    { key: 'email', label: 'Email Notifications', icon: Mail },
                    { key: 'push', label: 'Push Notifications', icon: Bell },
                    { key: 'sms', label: 'SMS Notifications', icon: Phone },
                  ].map(item => (
                    <div
                      key={item.key}
                      className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-700 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                          <item.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <span className="font-medium text-secondary-900 dark:text-white">
                          {item.label}
                        </span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={
                            preferencesForm.notifications[
                              item.key as keyof typeof preferencesForm.notifications
                            ]
                          }
                          onChange={e =>
                            setPreferencesForm({
                              ...preferencesForm,
                              notifications: {
                                ...preferencesForm.notifications,
                                [item.key]: e.target.checked,
                              },
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-secondary-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-secondary-600 peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                  ))}
                </div>

                <button className="bg-gradient-to-r from-primary-600 to-purple-600 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-200 font-semibold">
                  <Save className="w-5 h-5" />
                  Save Preferences
                </button>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="space-y-6 animate-fade-in">
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-white pb-4 border-b border-secondary-200 dark:border-secondary-700">
                  General Preferences
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Theme
                    </label>
                    <select
                      value={preferencesForm.theme}
                      onChange={e =>
                        setPreferencesForm({ ...preferencesForm, theme: e.target.value })
                      }
                      className="input"
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="system">System</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Language
                    </label>
                    <select
                      value={preferencesForm.language}
                      onChange={e =>
                        setPreferencesForm({ ...preferencesForm, language: e.target.value })
                      }
                      className="input"
                    >
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="fr">Français</option>
                      <option value="de">Deutsch</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-secondary-700 dark:text-secondary-300">
                      Timezone
                    </label>
                    <select
                      value={preferencesForm.timezone}
                      onChange={e =>
                        setPreferencesForm({ ...preferencesForm, timezone: e.target.value })
                      }
                      className="input"
                    >
                      <option value="UTC">UTC</option>
                      <option value="Europe/London">London (GMT)</option>
                      <option value="America/New_York">New York (EST)</option>
                      <option value="America/Los_Angeles">Los Angeles (PST)</option>
                    </select>
                  </div>
                </div>

                <button className="bg-gradient-to-r from-primary-600 to-purple-600 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-200 font-semibold">
                  <Save className="w-5 h-5" />
                  Save Preferences
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
